import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, Toast, Skeleton } from "react-vant";
import CommonNavBar from "views/components/CommonNavBar";
import { getMyWeakness, getMyRecommend, getMyTips } from "@/api/weakness";
import styles from "./MyWeakness.module.less";

const TREND_MAP = {
  improving: { text: "↗ 好转中", cls: styles.trendDown },
  worsening: { text: "↘ 需关注", cls: styles.trendUp },
  stable: { text: "— 稳定", cls: styles.trendStable },
};

function barColor(score, maxScore) {
  const ratio = maxScore > 0 ? score / maxScore : 0;
  if (ratio > 0.7) return "#f53f3f";
  if (ratio > 0.4) return "#ff7d00";
  return "#165dff";
}

function GrammarCard({ item, maxScore }) {
  const trend = TREND_MAP[item.trend] || TREND_MAP.stable;
  const pct =
    maxScore > 0 ? Math.min(100, (item.decay_score / maxScore) * 100) : 0;

  return (
    <div className={styles.weakCard}>
      <div className={styles.weakHeader}>
        <span className={styles.weakTitle}>
          {item.name_zh}
          <span style={{ fontSize: 12, color: "#86909c", marginLeft: 6 }}>
            {item.tag}
          </span>
        </span>
        <span className={styles.weakScore}>{item.decay_score}</span>
      </div>
      <div className={styles.weakBarWrap}>
        <div
          className={styles.weakBar}
          style={{
            width: `${pct}%`,
            background: barColor(item.decay_score, maxScore),
          }}
        />
      </div>
      <div className={styles.weakMeta}>
        <span>
          近30天 {item.error_count_30d} 次 / 总计 {item.error_count_total} 次
        </span>
        <span className={trend.cls}>{trend.text}</span>
      </div>
      {item.examples && item.examples.length > 0 && (
        <div className={styles.exampleWrap}>
          {item.examples.map((ex, i) => (
            <div key={i} className={styles.exampleItem}>
              <span className={styles.exampleOriginal}>{ex.original}</span>
              <span className={styles.exampleCorrection}>
                → {ex.correction}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VocabCard({ item, maxScore }) {
  const trend = TREND_MAP[item.trend] || TREND_MAP.stable;
  const pct =
    maxScore > 0 ? Math.min(100, (item.decay_score / maxScore) * 100) : 0;

  return (
    <div className={styles.weakCard}>
      <div className={styles.weakHeader}>
        <span className={styles.weakTitle}>
          <span className={styles.vocabWord}>{item.word}</span>
          {item.correction && (
            <>
              <span className={styles.vocabArrow}>→</span>
              <span className={styles.vocabCorrection}>{item.correction}</span>
            </>
          )}
        </span>
        <span className={styles.weakScore}>{item.decay_score}</span>
      </div>
      <div className={styles.weakBarWrap}>
        <div
          className={styles.weakBar}
          style={{
            width: `${pct}%`,
            background: barColor(item.decay_score, maxScore),
          }}
        />
      </div>
      <div className={styles.weakMeta}>
        <span>
          近30天 {item.error_count_30d} 次 / 总计 {item.error_count_total} 次
        </span>
        <span className={trend.cls}>{trend.text}</span>
      </div>
    </div>
  );
}

function TipsCarousel({ tips }) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    if (tips.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % tips.length);
    }, 3000);
  }, [tips.length]);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  if (!tips || tips.length === 0) return null;

  const tip = tips[current];

  return (
    <div className={styles.carouselWrap}>
      <div className={styles.carouselHeader}>
        <span className={styles.carouselIcon}>📝</span>
        <span className={styles.carouselLabel}>课堂回放</span>
        <span className={styles.carouselIndex}>
          {current + 1}/{tips.length}
        </span>
      </div>
      <div className={styles.carouselBody} key={current}>
        <div className={styles.carouselTitle}>{tip.title}</div>
        {tip.type === "grammar" ? (
          <>
            <div className={styles.carouselRow}>
              <span className={styles.carouselRowLabel}>原句</span>
              <span className={styles.carouselOriginal}>{tip.original}</span>
            </div>
            <div className={styles.carouselRow}>
              <span className={styles.carouselRowLabel}>纠正</span>
              <span className={styles.carouselCorrection}>
                {tip.correction}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className={styles.carouselRow}>
              <span className={styles.carouselRowLabel}>错词</span>
              <span className={styles.carouselOriginal}>
                {tip.original}
                <span className={styles.carouselArrow}> → </span>
                <span className={styles.carouselCorrection}>
                  {tip.correction}
                </span>
              </span>
            </div>
            {tip.context && (
              <div className={styles.carouselRow}>
                <span className={styles.carouselRowLabel}>原句</span>
                <span className={styles.carouselContext}>{tip.context}</span>
              </div>
            )}
          </>
        )}
        {tip.explanation && (
          <div className={styles.carouselExplain}>{tip.explanation}</div>
        )}
      </div>
    </div>
  );
}

function RecommendSection({ items }) {
  if (!items || items.length === 0) return null;

  return (
    <div className={styles.recommendSection}>
      <div className={styles.recommendTitle}>💡 建议优先练习</div>
      {items.map((item, i) => (
        <div key={item.tag} className={styles.recommendCard}>
          <div className={styles.recommendRank}>{i + 1}</div>
          <div className={styles.recommendBody}>
            <div className={styles.recommendName}>{item.name_zh}</div>
            <div className={styles.recommendReason}>{item.reason}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyWeakness() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [weakness, setWeakness] = useState(null);
  const [recommend, setRecommend] = useState(null);
  const [tips, setTips] = useState([]);
  const [activeTab, setActiveTab] = useState("grammar");

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [weakRes, recRes, tipsRes] = await Promise.all([
        getMyWeakness(),
        getMyRecommend(0),
        getMyTips(),
      ]);

      if (weakRes?.code === 200) {
        setWeakness(weakRes.data);
      }
      if (recRes?.code === 200) {
        setRecommend(recRes.data?.grammar_recommend || []);
      }
      if (tipsRes?.code === 200) {
        setTips(tipsRes.data?.tips || []);
      }
    } catch {
      Toast.fail("数据加载失败");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <CommonNavBar
          title="我的学习弱项"
          backgroundColor="#f0f5ff"
          onClickBack={() => navigate(-1)}
        />
        <div style={{ padding: 16 }}>
          <Skeleton title round row={6} />
        </div>
      </div>
    );
  }

  if (!weakness) {
    return (
      <div className={styles.container}>
        <CommonNavBar
          title="我的学习弱项"
          backgroundColor="#f0f5ff"
          onClickBack={() => navigate(-1)}
        />
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>📚</div>
          <div>暂无学习数据</div>
          <div style={{ marginTop: 8, fontSize: 12 }}>
            上完课后这里会展示你的学习分析
          </div>
        </div>
      </div>
    );
  }

  const { grammar_weakness = [], vocab_weakness = [], summary = {} } = weakness;
  const grammarMax =
    grammar_weakness.length > 0 ? grammar_weakness[0].decay_score : 1;
  const vocabMax =
    vocab_weakness.length > 0 ? vocab_weakness[0].decay_score : 1;

  return (
    <div className={styles.container}>
      <CommonNavBar
        title="我的学习弱项"
        backgroundColor="#f0f5ff"
        onClickBack={() => navigate(-1)}
      />

      {/* Summary Card */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>
              {summary.total_lessons || 0}
            </div>
            <div className={styles.summaryLabel}>累计课时</div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>
              {summary.total_grammar_errors || 0}
            </div>
            <div className={styles.summaryLabel}>语法错误</div>
          </div>
          <div className={styles.summaryItem}>
            <div className={styles.summaryValue}>
              {summary.total_vocab_errors || 0}
            </div>
            <div className={styles.summaryLabel}>词汇错误</div>
          </div>
        </div>
        <div className={styles.summaryBottom}>
          <span>
            最大弱项
            <span className={styles.summaryTag}>
              {summary.top_weakness || "-"}
            </span>
          </span>
          <span>
            近期进步
            <span className={styles.summaryTag}>
              {summary.most_improved || "-"}
            </span>
          </span>
        </div>
      </div>

      {/* Tip */}
      <div className={styles.tipCard}>
        <div className={styles.tipTitle}>如何看这个页面？</div>
        <div className={styles.tipText}>
          <b>得分</b>
          ：反映某类错误"当前是否仍是问题"。近期犯的错得分高，很久没犯的会自然消退。
        </div>
        <div className={styles.tipText}>
          <b>趋势</b>：对比近 30 天和之前的表现 ——
          "好转中"说明你在进步，"需关注"说明最近犯得更多了。
        </div>
        <div className={styles.tipText}>
          <b>推荐练习</b>
          ：综合你的个人弱项、语法难度、全站学员常犯错误排出优先级。
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsWrap}>
        <Tabs
          style={{ marginBottom: "0.5rem" }}
          active={activeTab}
          onChange={(name) => setActiveTab(name)}
        >
          <Tabs.TabPane
            title={`语法弱项 (${grammar_weakness.length})`}
            name="grammar"
          />
          <Tabs.TabPane
            title={`错词记录 (${vocab_weakness.length})`}
            name="vocab"
          />
        </Tabs>
      </div>

      {/* List */}
      <div className={styles.weakList}>
        {activeTab === "grammar" && (
          <>
            {grammar_weakness.length === 0 ? (
              <div className={styles.emptyWrap}>暂无语法错误记录</div>
            ) : (
              grammar_weakness.map((item) => (
                <GrammarCard key={item.tag} item={item} maxScore={grammarMax} />
              ))
            )}
          </>
        )}
        {activeTab === "vocab" && (
          <>
            {vocab_weakness.length === 0 ? (
              <div className={styles.emptyWrap}>暂无词汇错误记录</div>
            ) : (
              vocab_weakness.map((item, i) => (
                <VocabCard
                  key={`${item.word}-${i}`}
                  item={item}
                  maxScore={vocabMax}
                />
              ))
            )}
          </>
        )}
      </div>

      {/* Tips Carousel */}
      {activeTab === "grammar" && tips.length > 0 && (
        <TipsCarousel tips={tips} />
      )}

      {/* Recommend */}
      {activeTab === "grammar" && <RecommendSection items={recommend} />}
    </div>
  );
}
